/**
 * ComplyIQ API Module
 *
 * WHY: Provides typed API client for ComplyIQ compliance and audit management.
 * Follows the same patterns as the SoftwareIQ module.
 */

import { http } from '../core/client'

// ============================================================================
// Types
// ============================================================================

export type ControlStatus =
  | 'not_assessed'
  | 'compliant'
  | 'partially_compliant'
  | 'non_compliant'
  | 'not_applicable'

export type AssessmentStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'

export type AuditStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'

export type FindingSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'informational'

export type FindingStatus =
  | 'open'
  | 'in_remediation'
  | 'closed'
  | 'risk_accepted'

export type EvidenceType =
  | 'document'
  | 'screenshot'
  | 'log'
  | 'configuration'
  | 'other'

export interface Control {
  control_id: string
  framework: string
  control_number: string
  title: string
  description?: string
  status: ControlStatus
  mission_id?: string
  created_at: string
  updated_at: string
}

export interface CreateControlPayload {
  framework: string
  control_number: string
  title: string
  description?: string
  mission_id?: string
}

export interface Assessment {
  assessment_id: string
  name: string
  description?: string
  framework: string
  status: AssessmentStatus
  mission_id?: string
  scheduled_date?: string
  completed_date?: string
  assessor?: string
  created_at: string
  updated_at: string
}

export interface CreateAssessmentPayload {
  name: string
  description?: string
  framework: string
  mission_id?: string
  scheduled_date?: string
  assessor?: string
}

export interface Evidence {
  evidence_id: string
  control_id: string
  name: string
  description?: string
  evidence_type: EvidenceType
  file_path?: string
  url?: string
  mission_id?: string
  collected_by?: string
  collected_at: string
  created_at: string
}

export interface CreateEvidencePayload {
  control_id: string
  name: string
  description?: string
  evidence_type: EvidenceType
  file_path?: string
  url?: string
  mission_id?: string
}

export interface Audit {
  audit_id: string
  name: string
  description?: string
  audit_type: string
  status: AuditStatus
  mission_id?: string
  start_date?: string
  end_date?: string
  auditor?: string
  created_at: string
  updated_at: string
}

export interface CreateAuditPayload {
  name: string
  description?: string
  audit_type: string
  mission_id?: string
  start_date?: string
  end_date?: string
  auditor?: string
}

export interface AuditFinding {
  finding_id: string
  audit_id: string
  title: string
  description?: string
  severity: FindingSeverity
  status: FindingStatus
  control_id?: string
  remediation_plan?: string
  due_date?: string
  mission_id?: string
  created_at: string
  updated_at: string
}

export interface CreateAuditFindingPayload {
  audit_id: string
  title: string
  description?: string
  severity: FindingSeverity
  control_id?: string
  remediation_plan?: string
  due_date?: string
  mission_id?: string
}

export interface ComplyIQStats {
  total_controls: number
  compliant_controls: number
  compliance_score: number
  pending_assessments: number
  active_audits: number
  open_findings: number
  critical_findings: number
}

export interface SspRedactionCategory {
  id: string
  label: string
  description: string
}

export interface SspRedactionOptions {
  categories: SspRedactionCategory[]
  saved_llm_connections: SspRedactionLlmConnection[]
}

export interface SspRedactionLlmConnection {
  config_id: string
  display_name: string
  provider: string
  url: string
  model: string
  timeout_seconds: number
  verify_tls: boolean
}

export interface SspRedactionLlmCheck {
  ok: boolean
  message: string
  models: string[]
  model?: string
}

export interface SspRedactionJob {
  job_id: string
  run_name: string
  status: 'queued' | 'running' | 'complete' | 'error'
  message: string
  phase: string
  progress_percent: number
  findings_count: number | null
  field_count: number | null
  redaction_span_count: number | null
  elapsed_seconds: number
  error: string | null
}

export interface SspRedactionRect {
  id: string
  page: number
  x: number
  y: number
  width: number
  height: number
  source: 'manifest' | 'manual_visual'
}

export interface SspRedactionPreviewPage {
  page: number
  width: number
  height: number
  rects: SspRedactionRect[]
}

export interface SspRedactionPreview {
  ok: boolean
  run_name: string
  saved: boolean
  saved_at_utc?: string
  pages: SspRedactionPreviewPage[]
}

// ============================================================================
// API Base URL
// ============================================================================

const COMPLYIQ_BASE = '/api/complyiq'

async function complyIqFetch<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${COMPLYIQ_BASE}${endpoint}`, {
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  })
  if (!response.ok) {
    throw new Error(await response.text())
  }
  return response.json()
}

// ============================================================================
// Controls API
// ============================================================================

export const controlsApi = {
  list: async (params?: {
    missionId?: string
    framework?: string
    status?: ControlStatus
  }): Promise<Control[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.framework) searchParams.set('framework', params.framework)
    if (params?.status) searchParams.set('status', params.status)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; controls: Control[] }>(
      `${COMPLYIQ_BASE}/controls${query ? `?${query}` : ''}`
    )
    return response.controls || []
  },

  get: async (controlId: string): Promise<Control> => {
    return http.get(`${COMPLYIQ_BASE}/controls/${controlId}`)
  },

  create: async (payload: CreateControlPayload): Promise<Control> => {
    return http.post(`${COMPLYIQ_BASE}/controls`, payload)
  },

  update: async (controlId: string, payload: CreateControlPayload): Promise<Control> => {
    return http.put(`${COMPLYIQ_BASE}/controls/${controlId}`, payload)
  },

  delete: async (controlId: string): Promise<void> => {
    return http.delete(`${COMPLYIQ_BASE}/controls/${controlId}`)
  },
}

// ============================================================================
// Assessments API
// ============================================================================

export const assessmentsApi = {
  list: async (params?: {
    missionId?: string
    status?: AssessmentStatus
  }): Promise<Assessment[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.status) searchParams.set('status', params.status)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; assessments: Assessment[] }>(
      `${COMPLYIQ_BASE}/assessments${query ? `?${query}` : ''}`
    )
    return response.assessments || []
  },

  get: async (assessmentId: string): Promise<Assessment> => {
    return http.get(`${COMPLYIQ_BASE}/assessments/${assessmentId}`)
  },

  create: async (payload: CreateAssessmentPayload): Promise<Assessment> => {
    return http.post(`${COMPLYIQ_BASE}/assessments`, payload)
  },
}

// ============================================================================
// Evidence API
// ============================================================================

export const evidenceApi = {
  list: async (params?: {
    missionId?: string
    controlId?: string
    evidenceType?: EvidenceType
  }): Promise<Evidence[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.controlId) searchParams.set('control_id', params.controlId)
    if (params?.evidenceType) searchParams.set('evidence_type', params.evidenceType)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; evidence: Evidence[] }>(
      `${COMPLYIQ_BASE}/evidence${query ? `?${query}` : ''}`
    )
    return response.evidence || []
  },

  create: async (payload: CreateEvidencePayload): Promise<Evidence> => {
    return http.post(`${COMPLYIQ_BASE}/evidence`, payload)
  },
}

// ============================================================================
// Audits API
// ============================================================================

export const auditsApi = {
  list: async (params?: {
    missionId?: string
    status?: AuditStatus
    auditType?: string
  }): Promise<Audit[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.auditType) searchParams.set('audit_type', params.auditType)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; audits: Audit[] }>(
      `${COMPLYIQ_BASE}/audits${query ? `?${query}` : ''}`
    )
    return response.audits || []
  },

  get: async (auditId: string): Promise<Audit> => {
    return http.get(`${COMPLYIQ_BASE}/audits/${auditId}`)
  },

  create: async (payload: CreateAuditPayload): Promise<Audit> => {
    return http.post(`${COMPLYIQ_BASE}/audits`, payload)
  },
}

// ============================================================================
// Audit Findings API
// ============================================================================

export const auditFindingsApi = {
  list: async (params?: {
    missionId?: string
    auditId?: string
    severity?: FindingSeverity
    status?: FindingStatus
  }): Promise<AuditFinding[]> => {
    const searchParams = new URLSearchParams()
    if (params?.missionId) searchParams.set('mission_id', params.missionId)
    if (params?.auditId) searchParams.set('audit_id', params.auditId)
    if (params?.severity) searchParams.set('severity', params.severity)
    if (params?.status) searchParams.set('status', params.status)
    const query = searchParams.toString()
    const response = await http.get<{ count: number; findings: AuditFinding[] }>(
      `${COMPLYIQ_BASE}/audit-findings${query ? `?${query}` : ''}`
    )
    return response.findings || []
  },

  get: async (findingId: string): Promise<AuditFinding> => {
    return http.get(`${COMPLYIQ_BASE}/audit-findings/${findingId}`)
  },

  create: async (payload: CreateAuditFindingPayload): Promise<AuditFinding> => {
    return http.post(`${COMPLYIQ_BASE}/audit-findings`, payload)
  },

  updateStatus: async (findingId: string, status: FindingStatus): Promise<AuditFinding> => {
    return http.put(`${COMPLYIQ_BASE}/audit-findings/${findingId}/status?status=${status}`, {})
  },
}

// ============================================================================
// Stats API
// ============================================================================

export const complyiqStatsApi = {
  get: async (missionId?: string): Promise<ComplyIQStats> => {
    const query = missionId ? `?mission_id=${missionId}` : ''
    return http.get(`${COMPLYIQ_BASE}/stats${query}`)
  },
}

// ============================================================================
// SSP Foreign Disclosure Redaction API
// ============================================================================

export const sspRedactionsApi = {
  options: async (): Promise<SspRedactionOptions> => {
    return complyIqFetch('/ssp-redactions/options')
  },

  start: async (payload: {
    sourceFile: File
    redactionName?: string
    redactTypes: string[]
    manualDescription?: string
    llmConfigId: string
    llmModel?: string
    llmTimeout?: number
    ocr?: string
    ocrEngine?: string
    ocrLanguage?: string
  }): Promise<SspRedactionJob> => {
    const body = new FormData()
    body.set('source_file', payload.sourceFile)
    body.set('redaction_name', payload.redactionName || '')
    for (const type of payload.redactTypes) {
      body.append('redact_type', type)
    }
    body.set('manual_description', payload.manualDescription || '')
    body.set('llm_config_id', payload.llmConfigId)
    body.set('llm_model', payload.llmModel || '')
    body.set('llm_timeout', String(payload.llmTimeout || 240))
    body.set('ocr', payload.ocr || 'auto')
    body.set('ocr_engine', payload.ocrEngine || 'auto')
    body.set('ocr_language', payload.ocrLanguage || 'eng')

    const response = await fetch(`${COMPLYIQ_BASE}/ssp-redactions/runs`, {
      method: 'POST',
      body,
    })
    if (!response.ok) {
      throw new Error(await response.text())
    }
    return response.json()
  },

  saveConnection: async (payload: {
    displayName: string
    url: string
    model: string
    apiKeyValue?: string
    timeoutSeconds?: number
    verifyTls?: boolean
  }): Promise<{ ok: boolean; connection: SspRedactionLlmConnection }> => {
    return complyIqFetch('/ssp-redactions/llm-connections', {
      method: 'POST',
      body: JSON.stringify({
      display_name: payload.displayName,
      url: payload.url,
      model: payload.model,
      api_key_value: payload.apiKeyValue || '',
      timeout_seconds: payload.timeoutSeconds || 240,
      verify_tls: payload.verifyTls ?? true,
      }),
    })
  },

  deleteConnection: async (configId: string): Promise<{ ok: boolean; deleted_config_id: string }> => {
    return complyIqFetch(`/ssp-redactions/llm-connections/${encodeURIComponent(configId)}`, {
      method: 'DELETE',
    })
  },

  checkConnection: async (payload: {
    configId?: string
    displayName?: string
    url?: string
    model?: string
    apiKeyValue?: string
    timeoutSeconds?: number
    verifyTls?: boolean
  }): Promise<SspRedactionLlmCheck> => {
    return complyIqFetch('/ssp-redactions/llm-connections/check', {
      method: 'POST',
      body: JSON.stringify({
      config_id: payload.configId || '',
      display_name: payload.displayName || '',
      url: payload.url || '',
      model: payload.model || '',
      api_key_value: payload.apiKeyValue || '',
      timeout_seconds: payload.timeoutSeconds || 240,
      verify_tls: payload.verifyTls ?? true,
      }),
    })
  },

  job: async (jobId: string): Promise<SspRedactionJob> => {
    return complyIqFetch(`/ssp-redactions/jobs/${jobId}`)
  },

  preview: async (runName: string): Promise<SspRedactionPreview> => {
    return complyIqFetch(`/ssp-redactions/runs/${encodeURIComponent(runName)}/preview`)
  },

  previewPageUrl: (runName: string, pageNumber: number): string => {
    return `${COMPLYIQ_BASE}/ssp-redactions/runs/${encodeURIComponent(runName)}/preview/pages/${pageNumber}.png`
  },

  savePreview: async (runName: string, rectangles: SspRedactionRect[]): Promise<{ ok: boolean; saved_count: number }> => {
    return complyIqFetch(`/ssp-redactions/runs/${encodeURIComponent(runName)}/preview`, {
      method: 'POST',
      body: JSON.stringify({ rectangles }),
    })
  },

  downloadUrl: (runName: string, filename: 'redacted.pdf' | 'redaction_report.pdf'): string => {
    return `${COMPLYIQ_BASE}/ssp-redactions/runs/${encodeURIComponent(runName)}/downloads/${filename}`
  },
}
